/**
 * MbProduct Class
 * Handles Variant Switching (via Section Rendering API)
 * and Add to Cart (via Global Event Delegation)
 */
class MbProduct {
    constructor() {
        this.container = document.querySelector('[data-mb-product-hero]');
        if (!this.container) return;

        this.sectionId = this.container.dataset.sectionId;
        this.cartSections = ["mb-cart-drawer", "mb-cart", "header"];

        this.selectors = [
            '[data-mb-price-current]',      // Price text
            '[data-mb-variant-id]',         // Hidden ID input
            '[data-mb-atc]',                // Button classes (disabled/enabled)
            '[data-mb-atc-text]',           // Button text
            '[data-mb-condition-text]',     // Condition Panel Title
            '[data-mb-condition-features]', // Condition Panel Icons/List
            '[data-mb-product-inv]'         // Inventory JSON block
        ];

        this.init();
    }

    init() {
        // 1. Listen for Variant Changes (triggered by mb-product-variants-handler.js)
        this.container.addEventListener('mb:variant:change', (e) => {
            this.renderProductInfo(e.detail.variantId);
            this.updateBrowserURL(e.detail.variantId);
        });

        // 2. Listen for Add to Cart (Global Delegation - works for Service page too)
        document.addEventListener('submit', (e) => {
            const form = e.target.closest('[data-mb-product-form]');
            if (form) {
                e.preventDefault();
                this.handleAddToCart(form);
            }
        });
    }

    /**
     * Fetches new HTML from Shopify and surgically patches the DOM.
     */
    async renderProductInfo(variantId) {
        const url = `${window.location.pathname}?variant=${variantId}&section_id=${this.sectionId}`;

        try {
            const response = await fetch(url);
            const htmlText = await response.text();
            const fetchedHtml = new DOMParser().parseFromString(htmlText, 'text/html');

            this.selectors.forEach(selector => {
                const newEl = fetchedHtml.querySelector(selector);
                const currentEl = this.container.querySelector(selector);

                if (newEl && currentEl) {
                    if (currentEl.tagName === 'INPUT') {
                        currentEl.value = newEl.value;
                    } else {
                        currentEl.innerHTML = newEl.innerHTML;
                        currentEl.className = newEl.className;
                    }
                }
            });

            // Re-trigger any UI logic if needed (e.g., datepicker re-init)
            // if (window.mbDatePicker) window.mbDatePicker.init();

        } catch (e) {
            console.error("Shopify Section API error:", e);
        }
    }

    /**
     * Handles AJAX Add to Cart with inventory checks and success states.
     */
    async handleAddToCart(form) {
        const button = form.querySelector('[data-mb-atc]');
        if (!button || button.classList.contains('is-success')) return;

        const variantId = form.querySelector('[data-mb-variant-id]')?.value;

        // Perform Inventory Check
        const canAdd = await this.canAddAnother(variantId);
        if (!canAdd) {
            this.toggleQtyLimitMessage(true);
            return;
        }

        button.setAttribute('aria-busy', 'true');
        this.toggleQtyLimitMessage(false);

        try {
            const formData = new FormData(form);
            formData.append('sections', this.cartSections.join(','));

            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                body: formData,
                headers: { 'Accept': 'application/json' }
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.description || 'Error adding to cart');

            // Update Global Cart State
            document.dispatchEvent(new CustomEvent("mb:cart:changed", { detail: { cart: data } }));

            // Open the Drawer (using the bridge we added to mb-cart-drawer.js)
            if (window.mbCart) window.mbCart.open();

            // Success Animation State
            button.classList.add('is-success');
            setTimeout(() => button.classList.remove('is-success'), 2500);

        } catch (error) {
            console.error('ATC Error:', error);
            alert(error.message);
        } finally {
            button.setAttribute('aria-busy', 'false');
        }
    }

    async canAddAnother(variantId) {
        const cart = await fetch('/cart.js').then(r => r.json());

        const invEl = this.container.querySelector('[data-mb-product-inv]');
        if (!invEl) return true;
        const inv = JSON.parse(invEl.textContent);

        // --- THE PRODUCT-WIDE UNIQUE CHECK ---
        if (inv.isSingleStock) {
            // We look for ANY item in the cart where the handle matches
            const productAlreadyInCart = cart.items.find(item => item.handle === inv.productHandle);

            if (productAlreadyInCart) {
                console.log(`Unique product ${inv.productHandle} already in cart. Blocking additional variants.`);
                return false;
            }
        }

        // --- STANDARD VARIANT CHECK ---
        // (For regular products where you can have Red-Small and Blue-Small)
        const item = cart.items.find(i => i.id == variantId);
        const inCart = item?.quantity || 0;

        const isTracked = inv.management === "shopify";
        const deniesOversell = inv.policy === "deny";

        if (!isTracked || !deniesOversell) return true;

        const availableQty = inv.quantity || 0;
        return (inCart + 1) <= availableQty;
    }

    toggleQtyLimitMessage(show) {
        const msg = this.container.querySelector('.max-qty-warning');
        if (msg) msg.setAttribute("aria-hidden", show ? "false" : "true");
    }

    updateBrowserURL(variantId) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
    }
}

// Initialize on Load
document.addEventListener('DOMContentLoaded', () => {
    window.mbProductInstance = new MbProduct();
});
